import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, Globe, MessageSquarePlus } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGE_LABELS = {
  es: 'Español',
  en: 'English',
  fr: 'Français',
  zh: '中文',
  ar: 'العربية'
};

const ReviewCard = ({ review, language, index }) => {
  const [showOriginal, setShowOriginal] = useState(true);
  const [translatedText, setTranslatedText] = useState(null);
  const [translating, setTranslating] = useState(false);

  // Get comment - handle both old and new format
  const originalComment = review.comment_original || 
    (review.comment ? (typeof review.comment === 'object' ? Object.values(review.comment)[0] : review.comment) : '');
  
  const detectedLang = review.detected_language || 'unknown';
  const needsTranslation = detectedLang !== language && detectedLang !== 'unknown';

  const handleTranslate = async () => {
    if (translatedText) {
      setShowOriginal(!showOriginal);
      return;
    }

    setTranslating(true);
    try {
      const response = await axios.post(`${API}/reviews/translate`, {
        review_id: review.id,
        target_language: language
      });
      setTranslatedText(response.data.translated);
      setShowOriginal(false);
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setTranslating(false);
    }
  };

  const displayComment = showOriginal ? originalComment : (translatedText || originalComment);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow relative"
      data-testid={`review-card-${review.id}`}
    >
      {/* Quote icon */}
      <Quote className="absolute top-4 right-4 h-8 w-8 text-[#C2410C]/10" />

      {/* Featured badge */}
      {review.status === 'featured' && (
        <div className="absolute top-4 left-4 bg-[#C2410C] text-white text-xs px-2 py-1 rounded-full">
          Destacada
        </div>
      )}

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${
              i < review.rating 
                ? 'fill-[#F59E0B] text-[#F59E0B]' 
                : 'text-[#E7E5E4]'
            }`}
          />
        ))}
      </div>

      {/* Comment */}
      {displayComment && (
        <p 
          className="text-[#57534E] leading-relaxed mb-4 line-clamp-4"
          dir={detectedLang === 'ar' && showOriginal ? 'rtl' : 'ltr'}
        >
          "{displayComment}"
        </p>
      )}

      {/* Translation toggle */}
      {needsTranslation && originalComment && (
        <button
          onClick={handleTranslate}
          disabled={translating}
          className="text-xs text-[#C2410C] hover:underline flex items-center gap-1 mb-4"
        >
          <Globe className="h-3 w-3" />
          {translating ? 'Traduciendo...' : (showOriginal ? `Traducir a ${LANGUAGE_LABELS[language]}` : 'Ver original')}
        </button>
      )}

      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#C2410C] flex items-center justify-center text-white font-bold">
          {review.name.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-[#1C1917]">{review.name}</p>
          <p className="text-sm text-[#57534E]">
            {[review.country, review.created_at ? new Date(review.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short' }) : review.date].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const InlineReviewForm = ({ language }) => {
  const labels = {
    es: { cta: 'Deja tu valoración' },
    en: { cta: 'Leave a review' },
    fr: { cta: 'Laisser un avis' }
  };
  const l = labels[language] || labels.en;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
      <Button
        onClick={() => window.location.href = '/review'}
        className="bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-8 py-6 text-lg"
        data-testid="inline-review-cta"
      >
        <MessageSquarePlus className="h-5 w-5 mr-2" />
        {l.cta}
      </Button>
    </motion.div>
  );
};

const Reviews = () => {
  const { language, t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayLang, setDisplayLang] = useState(language);

  useEffect(() => {
    setDisplayLang(language);
  }, [language]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Calculate average rating
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 5.0;

  return (
    <section 
      id="reviews" 
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="reviews-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            {t.reviews?.title ? 'Testimonios' : 'Testimonios'}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917] mb-4">
            {t.reviews?.title || 'Lo que dicen nuestros viajeros'}
          </h2>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            {t.reviews?.subtitle || 'Miles de turistas han disfrutado de nuestros tours'}
          </p>

          {/* Average rating */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-[#F59E0B] text-[#F59E0B]" />
              ))}
            </div>
            <span className="text-2xl font-bold text-[#1C1917]">{avgRating}</span>
            <span className="text-[#57534E]">({reviews.length} reviews)</span>
          </div>

          {/* Language selector for translations */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Globe className="h-4 w-4 text-[#57534E]" />
            <span className="text-sm text-[#57534E]">Ver en:</span>
            <div className="flex gap-2">
              {['es', 'en', 'fr'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setDisplayLang(lang)}
                  className={`text-sm px-3 py-1 rounded-full transition-colors ${
                    displayLang === lang
                      ? 'bg-[#C2410C] text-white'
                      : 'bg-[#E5E5E5] text-[#57534E] hover:bg-[#D5D5D5]'
                  }`}
                >
                  {LANGUAGE_LABELS[lang]}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-64 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <ReviewCard
                key={review.id}
                review={review}
                language={displayLang}
                index={index}
              />
            ))}
          </div>
        )}

        {/* Inline Review Form */}
        <div className="mt-16" data-testid="inline-review-section">
          <InlineReviewForm language={displayLang} />
        </div>
      </div>
    </section>
  );
};

export default Reviews;
